<script setup lang="ts">
import { computed } from 'vue';
import {
    ChevronRight,
    Folder,
    Handshake,
    House,
    MoreHorizontal,
    Pin,
    PinOff,
} from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import LogoGlyph from '@/assets/logo-glyph.svg';
import Icon from '@/components/Icon.vue';
import WorkflowOverviewSheet from '@/components/sheets/WorkflowOverviewSheet.vue';
import { Button } from '@/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubAction,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import useApi from '@/composables/useApi.ts';
import useApp from '@/composables/useApp.ts';
import useSheet from '@/composables/useSheet.ts';
import type { PinnedSidebarItem, SidebarItems } from '@/types';
import type { WorkflowGroup } from '@/types/api';

defineProps<{
    items: SidebarItems;
}>();

const app = useApp();
const uri = computed(() => window.location.pathname);

// The employer portal is served from a subpath of the associates portal, so a
// bare '/dashboard' would leave that site. Compare paths rather than the full
// URLs, since `uri` only ever holds a path.
const dashboardUrl = computed(() => app.site.url + 'dashboard');
const isDashboard = computed(
    () => uri.value === new URL(dashboardUrl.value).pathname
);

const useFetch = useApi();
const sheet = useSheet();

async function pin(id: number) {
    const { data, error } = await useFetch<PinnedSidebarItem[]>('pin').post({
        id,
    });

    if (error.value) {
        toast.error('Menüeintrag konnte nicht angeheftet werden.');
        console.error(error.value);
        return;
    }

    app.pinnedSidebarItems = data.value as PinnedSidebarItem[];
    toast.success('Menüeintrag angeheftet.');
}

async function unpin(id: number) {
    const { data, error } = await useFetch<PinnedSidebarItem[]>('unpin').post({
        id,
    });

    if (error.value) {
        toast.error('Menüeintrag konnte nicht losgelöst werden.');
        console.error(error.value);
        return;
    }

    app.pinnedSidebarItems = data.value as PinnedSidebarItem[];
    toast.success('Menüeintrag wird nicht mehr angeheftet.');
}

async function openWorkflowSheet() {
    const { data, error } = await useFetch<WorkflowGroup[]>('workflows');

    if (error.value) {
        toast.error('Ein unerwarteter Fehler ist aufgetreten.');
        console.error(error.value);
        return;
    }

    await sheet.start({
        title: 'KlinikRente.Assistent',
        component: WorkflowOverviewSheet,
        props: {
            workflowGroups: data.value,
        },
    });
}
</script>
<template>
    <Sidebar>
        <SidebarHeader class="h-18 justify-center border-b px-6">
            <a
                :href="app.site.url"
                class="flex w-full items-center gap-x-4"
            >
                <span
                    class="bg-primary text-primary-foreground inline-flex size-10 items-center justify-center rounded-md p-2.5"
                >
                    <LogoGlyph class="h-auto w-full" />
                </span>
                <span class="text-base font-bold">{{ app.site.name }}</span>
            </a>
        </SidebarHeader>
        <SidebarContent>
            <SidebarGroup>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            as-child
                            :is-active="isDashboard"
                        >
                            <a :href="dashboardUrl">
                                <House />
                                <span>Dashboard</span>
                            </a>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarGroup>
            <SidebarGroup v-if="app.pinnedSidebarItems.length > 0">
                <SidebarGroupLabel>Angeheftet</SidebarGroupLabel>
                <SidebarGroupContent>
                    <SidebarMenu>
                        <SidebarMenuItem
                            v-for="pinnedItem in app.pinnedSidebarItems"
                            :key="pinnedItem.id"
                        >
                            <SidebarMenuButton as-child>
                                <a :href="pinnedItem.url">
                                    <Pin />
                                    <span>
                                        <span
                                            v-for="ancestor in pinnedItem.ancestors"
                                            class="text-muted-foreground truncate"
                                        >
                                            {{ ancestor }} ⁄
                                        </span>

                                        {{ pinnedItem.title }}
                                    </span>
                                </a>
                            </SidebarMenuButton>
                            <DropdownMenu>
                                <DropdownMenuTrigger as-child>
                                    <SidebarMenuAction show-on-hover>
                                        <MoreHorizontal />
                                    </SidebarMenuAction>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    side="right"
                                    align="start"
                                >
                                    <DropdownMenuItem
                                        @select="() => unpin(pinnedItem.id)"
                                    >
                                        <PinOff />
                                        <span>Nicht mehr anheften</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarGroupContent>
            </SidebarGroup>
            <SidebarGroup v-for="group in items">
                <SidebarGroupLabel v-if="group?.label">{{
                    group.label
                }}</SidebarGroupLabel>
                <SidebarGroupContent>
                    <SidebarMenu>
                        <template
                            v-for="groupItem in group?.items"
                            :key="groupItem.id"
                        >
                            <SidebarMenuItem
                                v-if="groupItem?.items === undefined"
                            >
                                <SidebarMenuButton
                                    as-child
                                    :is-active="groupItem.isActive"
                                >
                                    <a :href="groupItem.url">
                                        <Icon
                                            v-if="groupItem.icon"
                                            :icon="groupItem.icon"
                                        />
                                        <span>{{ groupItem.title }}</span>
                                    </a>
                                </SidebarMenuButton>
                                <DropdownMenu>
                                    <DropdownMenuTrigger as-child>
                                        <SidebarMenuAction show-on-hover>
                                            <MoreHorizontal />
                                        </SidebarMenuAction>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        side="right"
                                        align="start"
                                    >
                                        <DropdownMenuItem
                                            v-if="
                                                app.pinnedSidebarItems.some(
                                                    (item) =>
                                                        item.id === groupItem.id
                                                )
                                            "
                                            @select="() => unpin(groupItem.id)"
                                        >
                                            <PinOff />
                                            <span>Nicht mehr anheften</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            v-else
                                            @select="() => pin(groupItem.id)"
                                        >
                                            <Pin />
                                            <span>Anheften</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </SidebarMenuItem>
                            <Collapsible
                                v-else
                                as-child
                                :default-open="groupItem.isExpanded ?? false"
                                class="group/collapsible"
                            >
                                <SidebarMenuItem :id="groupItem.id">
                                    <SidebarMenuButton
                                        as-child
                                        :tooltip="groupItem.title"
                                        :is-active="groupItem.isActive"
                                    >
                                        <a :href="groupItem.url">
                                            <Folder />
                                            <span>{{ groupItem.title }}</span>
                                        </a>
                                    </SidebarMenuButton>
                                    <CollapsibleTrigger as-child>
                                        <SidebarMenuAction>
                                            <ChevronRight
                                                class="transition duration-200 group-data-[state=open]/collapsible:rotate-90"
                                                :style="{
                                                    viewTransitionName: `collapsible-trigger-${groupItem.id}`,
                                                }"
                                            />
                                        </SidebarMenuAction>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <SidebarMenuSub>
                                            <SidebarMenuSubItem
                                                v-for="subItem in groupItem.items"
                                                :key="subItem.id"
                                                :id="subItem.id"
                                            >
                                                <SidebarMenuSubButton
                                                    as-child
                                                    :is-active="
                                                        subItem.isActive
                                                    "
                                                >
                                                    <a :href="subItem.url">
                                                        <span>{{
                                                            subItem.title
                                                        }}</span>
                                                    </a>
                                                </SidebarMenuSubButton>

                                                <DropdownMenu>
                                                    <DropdownMenuTrigger
                                                        as-child
                                                    >
                                                        <SidebarMenuSubAction
                                                            show-on-hover
                                                        >
                                                            <MoreHorizontal />
                                                        </SidebarMenuSubAction>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent
                                                        side="right"
                                                        align="start"
                                                    >
                                                        <DropdownMenuItem
                                                            v-if="
                                                                app.pinnedSidebarItems.some(
                                                                    (item) =>
                                                                        item.id ===
                                                                        subItem.id
                                                                )
                                                            "
                                                            @select="
                                                                () =>
                                                                    unpin(
                                                                        subItem.id
                                                                    )
                                                            "
                                                        >
                                                            <PinOff />
                                                            <span
                                                                >Nicht mehr
                                                                anheften</span
                                                            >
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            v-else
                                                            @select="
                                                                () =>
                                                                    pin(
                                                                        subItem.id
                                                                    )
                                                            "
                                                        >
                                                            <Pin />
                                                            <span
                                                                >Anheften</span
                                                            >
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </SidebarMenuSubItem>
                                        </SidebarMenuSub>
                                    </CollapsibleContent>
                                </SidebarMenuItem>
                            </Collapsible>
                        </template>
                    </SidebarMenu>
                </SidebarGroupContent>
            </SidebarGroup>

            <!-- Workflows only exist on the associates portal -->
            <SidebarGroup
                v-if="app.isAssociatesPortal"
                class="mt-auto"
            >
                <SidebarGroupContent>
                    <Button
                        class="w-full"
                        @click="openWorkflowSheet"
                    >
                        <Handshake />
                        Was möchten Sie tun?
                    </Button>
                </SidebarGroupContent>
            </SidebarGroup>
        </SidebarContent>
    </Sidebar>
</template>
